import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { saveGroups } from './utils';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import i18n from './i18n';

export const AuthContext = createContext(null);

const decodeToken = (token) => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(atob(base64).split('').map(c =>
      '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
    ).join(''));
    return JSON.parse(jsonPayload);
  } catch {
    return null;
  }
};

export const AuthProvider = ({ children }) => {
  const { t } = useTranslation();
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAdminUser, setIsAdminUser] = useState(false);
  const [userAttributes, setUserAttributes] = useState([]);

  // 그룹 정보를 저장하고 admin 여부를 React 상태로 반영
  const updateGroups = useCallback((groups) => {
    saveGroups(groups);
    setIsAdminUser(groups.some(g => g.group_name === 'admin'));
  }, []);

  // 토큰으로부터 그룹 정보 복원 (새로고침 시 admin 메뉴 유지)
  const fetchAttributes = useCallback(async (token) => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/v1/admin/abac/my-attributes`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        credentials: 'include',
      });
      if (res.ok) {
        const data = await res.json();
        if (data.success) {
          const attrs = data.attributes || [];
          setUserAttributes(attrs);
          // debug 속성 보유 시 eruda 디버깅 콘솔 활성화
          if (attrs.includes('debug') && !window._erudaInitialized) {
            import('eruda').then(({ default: eruda }) => {
              eruda.init();
              window._erudaInitialized = true;
            }).catch(() => {});
          }
        }
      }
    } catch (e) { /* non-critical */ }
  }, []);

  const restoreGroups = useCallback(async (token) => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/v1/user/user_groups`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include',
      });
      if (res.ok) {
        const data = await res.json();
        if (data.success) {
          updateGroups(data.groups || []);
        }
      }
      fetchAttributes(token);
    } catch (e) {
      // 그룹 복원 실패는 치명적이지 않음
    }
  }, [updateGroups, fetchAttributes]);

  const login = async (username, password) => {
    try {

      // API 호출 예시
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/v1/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ username, password }),
      });

      if (!response.ok) {
        throw new Error('Login failed');
      }

      const data = await response.json();
      const tokenPayload = decodeToken(data.token.accessToken);
      setUser({ ...data.user, user_id: tokenPayload?.user_id });
      setIsAuthenticated(true);
      localStorage.setItem('accessToken', data.token.accessToken);

      // 로그인 시 DB에 저장된 언어 설정으로 동기화
      if (data.user.preferred_language) {
        i18n.changeLanguage(data.user.preferred_language);
        localStorage.setItem('language', data.user.preferred_language);
      }

      if (data.hasPendingObserverRequests) {
        toast.info(t('auth.pendingObserverRequest'), {
          position: "top-right",
          autoClose: 5000,
        });
      }

      if (data.hasNewAcceptanceNotifications) {
        toast.success(t('auth.observerRequestAccepted'), {
          position: "top-right",
          autoClose: 5000,
        });
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/v1/observer/notifications`, {
          headers: { 'Authorization': `Bearer ${data.token.accessToken}` },
          credentials: 'include',
        });
      }

      // Get user's groups using access token
      const groupResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/v1/user/user_groups`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${data.token.accessToken}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include',
      });

      if (!groupResponse.ok) {
        throw new Error('Failed to fetch user groups');
      }

      const groupData = await groupResponse.json();

      fetchAttributes(data.token.accessToken);

      return groupData;

    } catch (error) {
      throw error;
    }
  };

  const logout = () => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/v1/auth/logout`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include',
      }).catch(() => {});
    }
    setUser(null);
    updateGroups([]);
    setUserAttributes([]);
    setIsAuthenticated(false);
    setIsAdminUser(false);
    localStorage.removeItem('accessToken');
    // eruda 디버깅 콘솔 제거
    if (window._erudaInitialized) {
      import('eruda').then(({ default: eruda }) => {
        eruda.destroy();
        window._erudaInitialized = false;
      }).catch(() => {});
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      const payload = decodeToken(token);
      if (payload && payload.username) {
        if (payload.exp && payload.exp < Math.floor(Date.now() / 1000)) {
          // Access token 만료 → refresh 시도
          refreshAccessToken();
        } else {
          setUser({ username: payload.username, user_id: payload.user_id });
          setIsAuthenticated(true);
          restoreGroups(token);
        }
      } else {
        logout();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refreshAccessToken = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/v1/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({}),
      });

      if (!response.ok) {
        throw new Error('Refresh failed');
      }

      const data = await response.json();
      if (data.success) {
        localStorage.setItem('accessToken', data.accessToken);
        // 새 access token으로 user 상태 복원
        const payload = decodeToken(data.accessToken);
        if (payload && payload.username) {
          setUser({ username: payload.username, user_id: payload.user_id });
          setIsAuthenticated(true);
          restoreGroups(data.accessToken);
        }
        return true;
      } else {
        throw new Error(data.message);
      }
    } catch (error) {
      console.error('Failed to refresh token', error);
      logout();
      return false;
    }
  };

  useEffect(() => {
    // Silent Refresh Logic
    const checkTokenAndRefresh = async () => {
      const accessToken = localStorage.getItem('accessToken');
      if (!accessToken) return;

      const payload = decodeToken(accessToken);
      if (!payload) {
        await refreshAccessToken();
        return;
      }

      const now = Math.floor(Date.now() / 1000);
      // Refresh if expires in less than 5 minutes
      if (payload.exp - now < 300) {
        await refreshAccessToken();
      }
    };

    // Check immediately on mount
    checkTokenAndRefresh();

    // Check periodically (every 1 minute)
    const intervalId = setInterval(checkTokenAndRefresh, 60 * 1000);

    return () => clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // authFetch에서 refresh 실패 시 발행하는 auth:logout 이벤트 수신
  useEffect(() => {
    const handleAuthLogout = () => {
      setUser(null);
      updateGroups([]);
      setUserAttributes([]);
      setIsAuthenticated(false);
      setIsAdminUser(false);
    };

    window.addEventListener('auth:logout', handleAuthLogout);
    return () => window.removeEventListener('auth:logout', handleAuthLogout);
  }, [updateGroups]);

  // Polling for notifications
  useEffect(() => {
    if (!isAuthenticated) return;

    const checkNotifications = async () => {
      try {
        const token = localStorage.getItem('accessToken');
        if (!token) return;

        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/v1/observer/notifications`, {
          headers: { 'Authorization': `Bearer ${token}` },
          credentials: 'include',
        });
        const data = await response.json();
        if (data.success && data.notifications.length > 0) {
          data.notifications.forEach(notif => {
            toast.success(t('auth.studentAcceptedObservation', { student: notif.student }), {
              position: "top-right",
              autoClose: 5000
            });
          });
        }
      } catch (e) {
        console.error("Failed to check notifications", e);
      }
    };

    const interval = setInterval(checkNotifications, 30000); // Check every 30 seconds
    return () => clearInterval(interval);
  }, [isAuthenticated]);

  const setTokensAndUser = (accessToken) => {
    localStorage.setItem('accessToken', accessToken);
    const payload = decodeToken(accessToken);
    if (payload && payload.username) {
      setUser({ username: payload.username });
      setIsAuthenticated(true);
    }
  };

  const hasAttribute = useCallback((key) => userAttributes.includes(key), [userAttributes]);

  const value = {
    user,
    isAuthenticated,
    isAdminUser,
    userAttributes,
    hasAttribute,
    login,
    logout,
    updateGroups,
    setTokensAndUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
